import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';

import { draftStore } from '../patients/drafts.ts';

// One way for a form to keep what is typed on this device (plan §7 Offline; ADR 0018):
// load the draft when the key is known, save after each change, delete it when the work
// is saved to Wodi. Drafts are per user and facility (the key says so), expire, and are
// deleted on sign-out with every other draft. Never put secrets or payment details in one.

export interface DeviceDraft<T> {
  value: T;
  /** Changes the value and saves it to this device. */
  setValue: (next: SetStateAction<T>) => void;
  /** True once any stored draft has been read (at once when storage isn't available). */
  loaded: boolean;
  /** True when the value came from a draft left on this device. */
  restored: boolean;
  /** False when the browser offers no storage; say nothing about saving then. */
  available: boolean;
  /** Deletes the draft and starts again from `next` (the initial value by default). */
  clear: (next?: T) => Promise<void>;
}

export interface DeviceDraftOptions<T> {
  /** Checks a stored draft; return null to ignore it (e.g. typed against an old revision). */
  accept?: (stored: unknown) => T | null;
}

/**
 * The key of a draft: kind, user, facility, then what it is for. A facility belongs to one
 * tenant, so the key is per tenant too.
 */
export function deviceDraftKey(
  kind: string,
  session: { user: { id: string }; facility: { id: string } } | null,
  ...ids: string[]
): string | null {
  if (session === null) return null;
  return [kind, session.user.id, session.facility.id, ...ids].join(':');
}

export function useDeviceDraft<T>(
  key: string | null,
  initial: T,
  options: DeviceDraftOptions<T> = {},
): DeviceDraft<T> {
  const store = draftStore();
  const [value, setState] = useState<T>(initial);
  const [loaded, setLoaded] = useState(!store.available || key === null);
  const [restored, setRestored] = useState(false);
  // Saved only after the person changes something, so opening a form writes nothing.
  const [changed, setChanged] = useState(false);
  const initialRef = useRef(initial);
  const acceptRef = useRef(options.accept);
  acceptRef.current = options.accept;

  useEffect(() => {
    if (key === null || !store.available) return undefined;
    let active = true;
    void store
      .get(key)
      .then((stored) => {
        if (!active || stored === undefined || stored === null) return;
        const accepted =
          acceptRef.current === undefined ? (stored as T) : acceptRef.current(stored);
        if (accepted === null) return;
        setState(accepted);
        setRestored(true);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [key, store]);

  useEffect(() => {
    if (!loaded || !changed || key === null || !store.available) return;
    void store.set(key, value).catch(() => undefined);
  }, [value, changed, loaded, key, store]);

  const setValue = useCallback((next: SetStateAction<T>) => {
    setChanged(true);
    setState(next);
  }, []);

  const clear = useCallback(
    async (next?: T) => {
      setChanged(false);
      setRestored(false);
      setState(next ?? initialRef.current);
      if (key !== null) await store.delete(key).catch(() => undefined);
    },
    [key, store],
  );

  return { value, setValue, loaded, restored, available: store.available, clear };
}
